import React, { createContext, useContext, useState, ReactNode } from 'react';
import { translations, Language, TranslationKey } from '../i18n';

interface LanguageContextProps {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: TranslationKey | string) => string;
  // Zdanie z miejscami na wartości: tf("Import {rows}", { rows: ... }). Po niemiecku liczba
  // i nazwa tabeli stoją gdzie indziej niż po angielsku - sklejanie kawałków dawało
  // np. "Möchten Sie wirklich importieren 1 Zeilen in Tabelle ..."
  tf: (key: TranslationKey | string, params: Record<string, string | number>) => string;
  // "1 Zeile" / "5 Zeilen"
  rows: (count: number) => string;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Language>((localStorage.getItem('adminLang') as Language) || 'de');

  const setLang = (newLang: Language) => {
    setLangState(newLang);
    localStorage.setItem('adminLang', newLang);
  };

  const t = (key: string): string => {
    return (translations[lang] as any)[key] || key;
  };

  const tf = (key: string, params: Record<string, string | number>): string =>
    t(key).replace(/\{(\w+)\}/g, (placeholder, name) => (name in params ? String(params[name]) : placeholder));

  const rows = (count: number): string => tf(count === 1 ? "{count} row" : "{count} rows", { count });

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tf, rows }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
